import React, { Component, ErrorInfo } from "react";
import { useRouteError } from "react-router-dom";
import ErrorHandlerPage from "@zenith/pages/ErrorHandlerPage/error-handler-page/error-handler-page";
import { captureException } from "@zenith/utils/sentry";

declare const window: Window & typeof globalThis;

const RELOAD_FLAG = 'autoReloadAttempted';

function isChunkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; message?: string };
  return (
    e.name === 'ChunkLoadError' ||
    !!e.message?.includes('dynamically imported module') ||
    !!e.message?.includes('error loading dynamically imported module') ||
    !!e.message?.includes('Failed to fetch dynamically imported module')
  );
}

function attemptChunkRecovery(): boolean {
  if (sessionStorage.getItem(RELOAD_FLAG) === 'true') return false;
  sessionStorage.setItem(RELOAD_FLAG, 'true');
  if ('caches' in window) {
    caches.keys().then(keys =>
      Promise.all(keys.map(k => caches.delete(k))).finally(() => globalThis.location.reload())
    );
  } else {
    globalThis.location.reload();
  }
  return true;
}

/**
 * A stale chunk is recovered from by clearing the caches and reloading, which
 * leaves nothing behind for anyone to look at. It is still reported, and
 * fingerprinted into one issue, because the rate of them is what says whether
 * that reload is working or whether the deploy is the problem.
 */
function reportChunkError(error: unknown) {
  captureException(error, {
    tags: { source: "chunk-load" },
    fingerprint: ["chunk-load"],
  });
}

// ─── Router error element (react-router-dom v6 errorElement) ─────────────────
// Used in routes.tsx as errorElement to catch errors thrown during navigation
// and lazy route loading inside the router, which bypass class error boundaries.
export function RouterChunkErrorBoundary() {
  const error = useRouteError();
  if (isChunkError(error)) {
    // Without this the recovery reload is the only trace of the error: Router's
    // error element handles it before Sentry's own boundary can.
    reportChunkError(error);
    attemptChunkRecovery();
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Updating application, please wait…
      </div>
    );
  }
  return <ErrorHandlerPage reload />;
}

// ─── Class error boundary (wraps React tree in app.tsx) ──────────────────────
type ChunkErrorBoundaryProps = { children: React.ReactNode };

class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, { hasError: boolean }> {
  private reloadAttempted = false;

  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: isChunkError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Caught by ChunkErrorBoundary:", error, info);
    reportChunkError(error);
    if (!this.reloadAttempted && isChunkError(error)) {
      this.reloadAttempted = true;
      attemptChunkRecovery();
    }
  }

  render() {
    if (this.state.hasError && sessionStorage.getItem(RELOAD_FLAG) === 'true') {
      return <ErrorHandlerPage reload />;
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
